// Where a handoff confirmation email lands.
//
// Public, because the person at the address may have no account -- a
// receptionist, a shared inbox. Confirming takes a button press rather than
// happening on load: mail scanners open every link in a message, and a scanner
// must not be able to agree to receive a business's leads.

import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import * as api from "../lib/api";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";
import { PRODUCT_NAME } from "../lib/brand";
import { Button, Card, ErrorBanner } from "../components/ui";

export default function ConfirmEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [address, setAddress] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.confirmHandoffEmail(token);
      setAddress(result.address);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-3xl items-center px-4 py-4">
          <Link to="/" aria-label={PRODUCT_NAME} className="flex">
            <Logo />
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-16">
        <Card>
          {address ? (
            <>
              <h1 className="text-lg font-semibold text-slate-900">Confirmed</h1>
              <p className="mt-2 text-sm text-slate-600">
                When a visitor asks the assistant for a person, their details will be emailed to{" "}
                <strong>{address}</strong>.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-lg font-semibold text-slate-900">Confirm handoff emails</h1>
              <p className="mt-2 text-sm text-slate-600">
                Confirm that this address should receive visitors' contact details whenever they ask an assistant
                built with {PRODUCT_NAME} for a person. If you weren't expecting this, close this page and nothing
                will be sent.
              </p>
              <div className="mt-4">
                <ErrorBanner message={error} />
              </div>
              <div className="mt-4">
                <Button onClick={() => void confirm()} busy={busy} disabled={!token}>
                  Confirm this address
                </Button>
              </div>
              {!token && (
                <p className="mt-3 text-xs text-slate-500">This link is missing its code. Open it from the email again.</p>
              )}
            </>
          )}
        </Card>
      </main>

      <Footer />
    </div>
  );
}
